(function () {
  "use strict";

  // --- 1. CONFIGURATION ---
  const POSSIBLE_BACKGROUND_COLORS = [
    "#8F663A", // Brown
    "#74554E", // Muted Red/Brown
    "#65803F", // Muted Green
    "#963E2A", // Dark Red
  ];
  const ACCENT_LIGHTNESS = 90;
  const PATTERN_LIGHTNESS = 35;
  const ACCENT_SATURATION_CAP = 60;
  const COLOR_SEMITRANSPARENT_FACTOR = "c9";

  // --- 2. DOM ELEMENTS ---
  const rootElement = document.documentElement;

  // 1. Select a random background color
  const randomIndex = getRandomIndex(POSSIBLE_BACKGROUND_COLORS.length);
  const selectedBgColor = POSSIBLE_BACKGROUND_COLORS[randomIndex];

  // 2. Calculate accent and pattern colors
  const accentColor = calculateAccentColor(selectedBgColor, ACCENT_LIGHTNESS);
  const patternColor = calculateAccentColor(selectedBgColor, PATTERN_LIGHTNESS);

  // --- 3. HELPER FUNCTIONS ---

  /**
   * Returns a random integer between 0 and max (exclusive).
   * @param {number} max - The upper bound.
   * @returns {number} A random index.
   */
  function getRandomIndex(max) {
    return Math.floor(Math.random() * max);
  }

  /**
   * Converts a Hex color string to an HSL array [h, s, l].
   * Handles 3 and 6 digit hex codes.
   * @param {string} hex - The hex color (e.g., "#RRGGBB" or "#RGB").
   * @returns {Array<number>} An array containing [hue, saturation, lightness].
   */
  function hexToHsl(hex) {
    let r = 0,
      g = 0,
      b = 0;

    if (hex.length === 4) {
      // #RGB
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      // #RRGGBB
      r = parseInt(hex.substring(1, 3), 16);
      g = parseInt(hex.substring(3, 5), 16);
      b = parseInt(hex.substring(5, 7), 16);
    }

    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0,
      l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return [h * 360, s * 100, l * 100];
  }

  /**
   * Converts an HSL array [h, s, l] back to a Hex color string.
   * @param {number} h - Hue (0-360).
   * @param {number} s - Saturation (0-100).
   * @param {number} l - Lightness (0-100).
   * @returns {string} The hex color string (e.g., "#RRGGBB").
   */
  function hslToHex(h, s, l) {
    h /= 360;
    s /= 100;
    l /= 100;
    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    return "#" + toHex(r) + toHex(g) + toHex(b);
  }

  /**
   * Calculates an accent color by adjusting the lightness and saturation of a base color.
   * @param {string} hex - The base hex color.
   * @param {number} targetLightness - The target lightness (0-100).
   * @returns {string} The resulting hex color.
   */
  function calculateAccentColor(hex, targetLightness) {
    let [h, s] = hexToHsl(hex);
    const newSaturation = Math.min(s, ACCENT_SATURATION_CAP);
    return hslToHex(h, newSaturation, targetLightness);
  }

  // --- 4. MAIN EXECUTION ---

  /**
   * Selects a random theme, calculates derived colors, and applies them to the document.
   */
  async function applyRandomTheme() {
    await loadSvg();
    const bgElements = document.querySelectorAll(".bg-pattern-element");

    bgElements.forEach((bgElement) => {
      try {
        const path = bgElement.querySelector("svg g path");
        if (path) {
          path.style.fill = patternColor;

          // Force a frame skip so the browser "sees" opacity: 0 first
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              bgElement.classList.add("loaded");
            });
          });
        }
      } catch (e) {
        console.error("Error accessing SVG document:", e);
      }
    });
  }

  function applyColorVariables() {
    // 3. Apply colors as CSS custom properties
    rootElement.style.setProperty("--background-color", selectedBgColor);
    rootElement.style.setProperty(
      "--background-color-transparent",
      selectedBgColor + COLOR_SEMITRANSPARENT_FACTOR,
    );
    rootElement.style.setProperty("--ghost-accent-color", accentColor);
    rootElement.style.setProperty("--ghost-pattern-color", patternColor);
    rootElement.style.setProperty(
      "--ghost-pattern-color-semitransparent",
      patternColor + COLOR_SEMITRANSPARENT_FACTOR,
    );
    rootElement.style.setProperty(
      "--ghost-pattern-color-transparent",
      patternColor + "00",
    );
  }

  // Pre-apply color variables
  applyColorVariables();

  // Load SVGs
  //
  // Load external SVG and inject into DOM
  async function loadSvg() {
    try {
      const response = await fetch("/assets/images/bg3.svg");
      const svgText = await response.text(); // Get SVG as text

      for (let i = 0; i < 10; i++) {
        const container = document.querySelector(".bg-main-pattern");
        const bgPatternElement = document.createElement("div");
        bgPatternElement.setAttribute("class", "bg-pattern-element");

        bgPatternElement.innerHTML = svgText; // Inject SVG into container
        container.appendChild(bgPatternElement);
      }
    } catch (error) {
      console.error("Failed to load SVG:", error);
    }
  }

  // --- 5. INITIALIZATION ---
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyRandomTheme);
  } else {
    applyRandomTheme();
  }
})();
